import {
	type ListInstrumentsRequest,
	type SelectableBasket,
	StandardInstrumentsControllerApiFactory,
	type Tag,
	type UserInstrumentClassificationDto,
	type UserInstrumentDto,
} from "@eu.mdotm.api/sphere";
import {
	type BaseHScrollTableProps,
	ButtonGroupRadio,
	CircularProgressBar,
	Dialog,
	DialogFooter,
	LocalOverlay,
	type OrderBy,
	Pagination,
	type PaginationResponse,
	Row,
	Text,
} from "@mdotm/shadcn/legacy/components";
import { type MultiSelectCtx, useMultiSelect } from "@mdotm/shadcn/legacy/headless";
import {
	adaptAnimatedNodeProvider,
	type SpawnResult,
	spawn,
	useDebounced,
	useDeepEqualEffect,
} from "@mdotm/shadcn/legacy/react-extensions";
import { useAtomValue } from "@mdotm/shadcn/legacy/reactivity";
import { BulkActions, Button, FormField, Label, SubmitButton } from "@mdotm/shadcn/mdotm/components";
import type { MaybePromise } from "@mdotm/utils/promise";
import { unreachable } from "@mdotm/utils/runtime";
import { useIsFetching, useQueries } from "@tanstack/react-query";
import type {
	ColDef,
	ColumnState,
	GridApi,
	GridReadyEvent,
	GridSizeChangedEvent,
	SelectionChangedEvent,
	SortChangedEvent,
	StateUpdatedEvent,
} from "ag-grid-community";
import type { AgGridReact } from "ag-grid-react";
import { Map, Set } from "immutable";
import { type Ref, useCallback, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useApiGen } from "$root/api/hooks";
import { IconWallBase } from "$root/components/IconWall";
import { ReactQueriesWrapperBase, ReactQueryWrapperBase } from "$root/components/ReactQueryWrapper";
import type { MinimumDialogProps } from "$root/components/spawnable/type";
import {
	createListInstrumentsRequest,
	enabledClassificationUuids,
	getPaginatedUserInstruments,
} from "$root/functional-areas/instruments/Instrument-customisation/helper";
import InstrumentCustomisationTableSidePanel, {
	type TagFilter,
} from "$root/functional-areas/instruments/Instrument-customisation/SidePanelColumnFilters";
import type { PaginatedUserInstrumentsRequestProps } from "$root/functional-areas/instruments/Instrument-customisation/types";
import { instrumentsRepository } from "$root/functional-areas/instruments/repository";
import { userClassifications$ } from "$root/functional-areas/instruments/user-classifications";
import { portfolioRepository } from "$root/functional-areas/portfolio/repository";
import { persistedColStateKeys } from "$root/functional-areas/user/userStorage";
import { usePaginationTools } from "$root/hooks/usePaginationTools";
import { AgGridWithModules, useAgGridRowListener } from "$root/third-party-integrations/ag-grid/ag-grid";
import { axiosExtract } from "$root/third-party-integrations/axios";
import {
	noRefetchDefaults,
	useMappedQuery,
	useQueryNoRefetch,
	useQueryReactor,
} from "$root/third-party-integrations/react-query/hooks";
import { queryClientKeys } from "$root/third-party-integrations/react-query/typedQueryClient";
import { columnsDefinition } from "$root/ui-lib/table/column";
import { agExtra } from "$root/ui-lib/table/default-configuration";
import { minutes, seconds } from "$root/utils";
import type { Instrument, InstrumentEntities } from "../../const";
import { type AddMissingInstrumentDialogProps, spawnInstrumentDialog } from "./missing-instrument-adder";
import { InstrumentPool, useInstrumentAdderState } from "./store";

type InstrumentAdderDialogProps = {
	entity: InstrumentEntities;
	/**
	 * Only useful for investments
	 */
	uuid?: string;
	onSubmitAsync(selectedTickers: string[] /** tickerName[] */): MaybePromise<void>;
	onSubmitMissingInstrument?: AddMissingInstrumentDialogProps["onSubmit"];
	// TODO: refactor to only take in the ticker (/tickerName) array
	instrumentsAlreadySelected: Instrument[];
	/** @default undefined (every pool is allowed) */
	allowedInstrumentPools?: InstrumentPool[];
};

/** @internal — exported for tests */
export function InstrumentAddersDialog(props: InstrumentAdderDialogProps & MinimumDialogProps) {
	const { show, onAnimationStateChange, onClose, ...rest } = props;
	const { entity, uuid, instrumentsAlreadySelected, onSubmitAsync } = rest;
	const { instrumentAdderStore, setInstrumentAdderStore } = useInstrumentAdderState();
	const classifications = useAtomValue(userClassifications$);
	const { t } = useTranslation();

	const queryStandardClassificationsRaw = instrumentsRepository.standardClassifications.useQuery();

	const queryStandardClassifications = useMappedQuery(
		(standardClassifications) => {
			const standardClassificationsWithUuid = standardClassifications.map(
				(classification): UserInstrumentClassificationDto => ({
					...classification,
					classificationUuid: classification.standardClassificationId,
				}),
			);
			const columnsClassifications = standardClassificationsWithUuid.filter(
				(col) =>
					col.standardClassificationId === "ISIN" ||
					col.standardClassificationId === "MACRO_ASSET_CLASS" ||
					col.standardClassificationId === "MICRO_ASSET_CLASS" ||
					col.standardClassificationId === "REGION",
			);
			return { columnsClassifications, standardClassificationsWithUuid };
		},
		[],
		queryStandardClassificationsRaw,
	);

	const commonInstrumentsQuery = useQueries({
		queries: [
			{
				...noRefetchDefaults,
				queryKey: [queryClientKeys.queryAddInstrumentsEntitySummary, uuid],
				async queryFn() {
					switch (entity) {
						case "INVESTMENT": {
							if (!uuid) {
								return { entity };
							}
							const { universeIdentifier } = await portfolioRepository.summary.fetchQuery(uuid, {
								enhanced: false,
							});
							return { uuid, universeIdentifier, entity };
						}
						case "INVESTMENT_ENHANCEMENT": {
							if (!uuid) {
								return { entity };
							}

							const { universeIdentifier } = await portfolioRepository.summary.fetchQuery(uuid, {
								enhanced: true,
							});
							return { uuid, universeIdentifier, entity };
						}
						case "INVESTMENT_DRAFT":
						case "TARGET_INVESTMENT":
						case "UNIVERSE":
						case "BENCHMARK": {
							if (!uuid) {
								return { entity };
							}

							return { uuid, entity };
						}
						default:
							unreachable(entity);
					}
				},
				staleTime: 0,
				cacheTime: minutes(5),
			},
			{
				...noRefetchDefaults,
				...instrumentsRepository.selectableBaskets.getQueryConfig(),
			},
		],
	});

	const instrumentsSetAlreadySelected = useMemo(
		() =>
			Set(
				instrumentsAlreadySelected.flatMap((x) =>
					x.tickerName && x.proxyOverwriteType !== "PORTFOLIO_MIXED" ? [x.tickerName] : [],
				),
			),
		[instrumentsAlreadySelected],
	);

	const selectionCtx = useMultiSelect<string>({
		// alwaysSelected: instrumentsSetAlreadySelected,
	});

	const queryInstrumentRowsKeyIsFetching = useIsFetching({
		predicate(query) {
			const queryKey = query.queryKey?.[0];
			return queryKey === queryClientKeys.queryInstrumentsAddableInComposition;
		},
	});

	const instrumentPool = useMemo(
		() =>
			!rest.allowedInstrumentPools
				? instrumentAdderStore.instrumentPool
				: rest.allowedInstrumentPools.includes(instrumentAdderStore.instrumentPool)
					? instrumentAdderStore.instrumentPool
					: rest.allowedInstrumentPools[0],
		[instrumentAdderStore.instrumentPool, rest.allowedInstrumentPools],
	);

	const standardInstrumentsApi = useApiGen(StandardInstrumentsControllerApiFactory);
	const standardInstrumentProvider = useCallback(
		async (params: PaginatedUserInstrumentsRequestProps): Promise<PaginationResponse<UserInstrumentDto>> => {
			const paginationResult = await axiosExtract(
				standardInstrumentsApi.listInstruments(createListInstrumentsRequest(params)),
			);

			return {
				items: paginationResult.items ?? [],
				total: paginationResult.pagination?.total ?? 0,
			};
		},
		[standardInstrumentsApi],
	);

	const rowsProvider = useMemo(() => {
		if (instrumentPool === InstrumentPool.mdotm) {
			return standardInstrumentProvider;
		}

		return getPaginatedUserInstruments;
	}, [instrumentPool, standardInstrumentProvider]);

	const projectedListProvider = useCallback<InstrumentCustomisationInnerProps["projectedListProvider"]>(
		async (params) => {
			if (instrumentPool === InstrumentPool.mdotm) {
				const result = await instrumentsRepository.projectedStandardInstruments.fetchQuery(params);
				return result.map((x) => x.tickerName!);
			}

			const result = await instrumentsRepository.projectedCustomInstruments.fetchQuery(params);
			return result.map((x) => x.tickerName!);
		},
		[instrumentPool],
	);

	return (
		<Dialog
			size="screen"
			noValidate
			show={show}
			onClose={onClose}
			header="Select"
			onAnimationStateChange={onAnimationStateChange}
			footer={
				<DialogFooter
					neutralAction={
						<Button palette="tertiary" onClick={onClose}>
							{t("BUTTON.CANCEL")}
						</Button>
					}
					primaryAction={
						<SubmitButton disabled={queryInstrumentRowsKeyIsFetching > 0}>{t("BUTTON.CONFIRM")}</SubmitButton>
					}
				/>
			}
			onSubmitAsync={() => onSubmitAsync(selectionCtx.selection.valueSeq().toArray())}
		>
			<div className="h-[calc(100dvh-214px)] flex-1 flex flex-col">
				<ReactQueriesWrapperBase queries={[...commonInstrumentsQuery, queryStandardClassifications]}>
					{([minimumSummary, selectableBaskets, standardClassifications]) => (
						<InstrumentBlockInner
							instrumentPool={instrumentPool}
							onChangeInstrumentPool={(newInstrumentsPool) => {
								setInstrumentAdderStore((latestVal) => ({
									...latestVal,
									instrumentPool: newInstrumentsPool,
								}));
							}}
							rowsProvider={rowsProvider}
							projectedListProvider={projectedListProvider}
							selectableBasketProvider={() => Promise.resolve(selectableBaskets)}
							selectionCtx={selectionCtx}
							minimumSummary={minimumSummary}
							selectableBaskets={selectableBaskets}
							instrumentsSetAlreadySelected={instrumentsSetAlreadySelected}
							classifications={
								instrumentPool === InstrumentPool.mdotm
									? standardClassifications.columnsClassifications
									: (classifications ?? [])
							}
							selectableUserClassificationsProvider={() =>
								Promise.resolve(
									instrumentPool === InstrumentPool.mdotm
										? standardClassifications.standardClassificationsWithUuid
										: (classifications ?? []),
								)
							}
							{...rest}
						/>
					)}
				</ReactQueriesWrapperBase>
			</div>
		</Dialog>
	);
}

type MinimumEntitySummary =
	| {
			uuid: string;
			universeIdentifier: string | undefined;
			entity: "INVESTMENT" | "INVESTMENT_ENHANCEMENT";
	  }
	| {
			uuid: string;
			entity: "UNIVERSE" | "BENCHMARK" | "TARGET_INVESTMENT" | "INVESTMENT_DRAFT";
	  }
	| {
			uuid?: undefined;
			entity:
				| "INVESTMENT"
				| "TARGET_INVESTMENT"
				| "INVESTMENT_DRAFT"
				| "INVESTMENT_ENHANCEMENT"
				| "UNIVERSE"
				| "BENCHMARK";
	  };

type SidePanelFilterState = {
	selectedBaskets: SelectableBasket[];
	selectedFilters: Immutable.Map<string, TagFilter>;
};

const defaultSidePanelFilters: SidePanelFilterState = {
	selectedBaskets: [],
	selectedFilters: Map(),
};

type InstrumentCustomisationInnerProps = {
	rowsProvider(request: PaginatedUserInstrumentsRequestProps): Promise<PaginationResponse<UserInstrumentDto>>;
	projectedListProvider(
		params: Pick<ListInstrumentsRequest, "keyword" | "tagValue" | "selectableBaskets">,
	): Promise<Array<string /** tickerName */>>;
	selectableBasketProvider(): Promise<SelectableBasket[]>;
	selectableUserClassificationsProvider(): Promise<Array<UserInstrumentClassificationDto>>;
	selectionCtx: MultiSelectCtx<string>;
	instrumentPool: InstrumentPool;
	minimumSummary: MinimumEntitySummary;
	onChangeInstrumentPool(newPool: InstrumentPool): void;
	instrumentsSetAlreadySelected: Set<string>;
	selectableBaskets: SelectableBasket[];
	classifications: UserInstrumentClassificationDto[];
} & InstrumentAdderDialogProps;

const poolOptions = [
	{
		value: InstrumentPool.mdotm,
		children: "MDOTM",
	},
	{
		value: InstrumentPool.instrumentCustomisation,
		children: "My Instruments",
	},
];
function InstrumentBlockInner({
	classifications,
	rowsProvider,
	selectableBasketProvider,
	selectableUserClassificationsProvider,
	selectionCtx,
	minimumSummary,
	instrumentPool,
	onChangeInstrumentPool,
	selectableBaskets,
	projectedListProvider,
	allowedInstrumentPools,
	...rest
}: InstrumentCustomisationInnerProps) {
	const [gridEl, setGridEl] = useState<AgGridReact<UserInstrumentDto> | null>(null);
	const [columnState, setColumnState] = useState<ColumnState[]>([]);
	useAgGridRowListener(gridEl, "stateUpdated", (event) => {
		const currentColumnState = event.api.getColumnState();
		setColumnState(() => currentColumnState);
	});

	const [userClassificationIds, setUserClassificationIds] = useState(() =>
		enabledClassificationUuids(classifications, columnState),
	);

	useDeepEqualEffect(() => {
		setUserClassificationIds((latestUserClassificationIds) => {
			const newUserClassificationIds = enabledClassificationUuids(classifications, columnState);
			const foundAllClassification = newUserClassificationIds.every((id) => latestUserClassificationIds.includes(id));
			if (foundAllClassification && newUserClassificationIds.length === latestUserClassificationIds.length) {
				return latestUserClassificationIds;
			}

			return newUserClassificationIds;
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [columnState, instrumentPool]);

	const initialColumns = useMemo(() => {
		if (instrumentPool === "MDOTM") {
			return columnsDefinition.instrumentStandardCustomisation(classifications);
		}
		return columnsDefinition.instrumentCustomisation(classifications, "read");
	}, [classifications, instrumentPool]);

	const alreadySelectedBasket = useMemo(() => {
		if (
			(minimumSummary.entity === "INVESTMENT_ENHANCEMENT" || minimumSummary.entity === "INVESTMENT") &&
			minimumSummary.uuid
		) {
			const matchedBasket = selectableBaskets.find((x) => x.basketIdentifier === minimumSummary.universeIdentifier);
			return matchedBasket ? [matchedBasket] : undefined;
		}
		return undefined;
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [minimumSummary.entity, minimumSummary.uuid, selectableBaskets]);

	const { instrumentAdderStore, setInstrumentAdderStore } = useInstrumentAdderState();
	const [sidePanelFilters, setSidePanelFilters] = useState<SidePanelFilterState>({
		...defaultSidePanelFilters,
		selectedBaskets: alreadySelectedBasket ?? [],
	});

	const { orderBy, query, skip, take } = usePaginationTools();

	const selectedFilters = useMemo<Tag[] | undefined>(() => {
		const filters = sidePanelFilters.selectedFilters?.valueSeq().toArray();

		return filters
			?.filter((filter) => filter.classificationUuid && filter.values && filter.values.length > 0)
			.map((x) => ({
				...x,
				...(instrumentPool === InstrumentPool.mdotm
					? {
							standardClassificationId: x.classificationUuid as Tag["standardClassificationId"],
						}
					: {}),
			}));
	}, [instrumentPool, sidePanelFilters.selectedFilters]);

	const queryInstrumentsRows = useQueryNoRefetch(
		[
			queryClientKeys.queryInstrumentsAddableInComposition,
			orderBy.value,
			query.value,
			skip.value,
			userClassificationIds,
			sidePanelFilters.selectedBaskets,
			selectedFilters,
			instrumentPool,
			take.value,
		],
		{
			queryFn() {
				return rowsProvider({
					skip: skip.value,
					take: take.value,
					orderBy: orderBy.value,
					filters: {
						query: query.value,
						fields: userClassificationIds,
						selectedBaskets: sidePanelFilters.selectedBaskets,
						tags: selectedFilters,
					},
				});
			},
			keepPreviousData: true,
			staleTime: minutes(5),
			cacheTime: minutes(5),
		},
	);

	const queryProjectedList = useQueryNoRefetch(
		["queryProjectedStandardAndCustomList", query.value, sidePanelFilters.selectedBaskets, selectedFilters],
		{
			keepPreviousData: true,
			queryFn() {
				return projectedListProvider({
					keyword: query.value,
					selectableBaskets: sidePanelFilters.selectedBaskets,
					tagValue: selectedFilters,
				});
			},
		},
	);

	const isRestoringSelection = useRef<boolean>(false);
	useQueryReactor(queryInstrumentsRows, {
		onSuccess() {
			const api = gridEl?.api;
			if (!api) {
				console.warn("ag grid not ready yet");
				return;
			}

			isRestoringSelection.current = true;

			syncAgSelectionWithCtx(api, selectionCtx);

			isRestoringSelection.current = false;
		},
	});

	const rows = useMemo(() => queryInstrumentsRows.data?.items ?? [], [queryInstrumentsRows.data?.items]);
	const total = useMemo(() => queryInstrumentsRows.data?.total ?? 0, [queryInstrumentsRows.data?.total]);

	const persistColsProps = agExtra.usePersistColState(
		instrumentPool === "INSTRUMENT_CUSTOMISATION"
			? persistedColStateKeys["tableV3-addCustomInstrument-read"]
			: persistedColStateKeys["tableV3-addStandardInstrument-read"],
	);

	return (
		<div className="flex-1 flex min-h-0 min-w-0 relative">
			<InstrumentCustomisationTableSidePanel
				key={instrumentPool}
				expand={instrumentAdderStore.expand}
				onExpandChange={() =>
					setInstrumentAdderStore((latest) => ({
						...latest,
						expand: !latest.expand,
					}))
				}
				selectableBasketProvider={selectableBasketProvider}
				selectableUserClassificationsProvider={selectableUserClassificationsProvider}
				initialInstrumentCustomisationSettings={{
					search: query.value,
					selectedBaskets: sidePanelFilters.selectedBaskets,
					selectedfilters: sidePanelFilters.selectedFilters,
				}}
				onChangeInstrumentCustomisationSettings={(newSettings) => {
					const { search, ...filters } = newSettings;
					query.set(search);
					setSidePanelFilters((latest) => ({
						...latest,
						selectedBaskets: filters.selectedBaskets ?? [],
						selectedFilters: filters.selectedfilters ?? Map(),
					}));
				}}
				noManLand={
					<FormField
						label={({ htmlFor }) => (
							<Row alignItems="center" gap={8}>
								<Label htmlFor={htmlFor} className="mb-px">
									Instrument pool
								</Label>
							</Row>
						)}
						className="mb-2"
					>
						<ButtonGroupRadio
							options={
								allowedInstrumentPools
									? poolOptions.filter((opt) => allowedInstrumentPools.includes(opt.value))
									: poolOptions
							}
							onChange={(newPool) => {
								onChangeInstrumentPool(newPool);
								setSidePanelFilters((latest) => ({
									...latest,
									selectedFilters: Map(),
								}));
							}}
							value={instrumentPool}
						/>
					</FormField>
				}
				basketSelection={
					alreadySelectedBasket
						? {
								disabled: true,
								hideDelete: true,
								customTriggerMultiSelection() {
									return alreadySelectedBasket.at(0)?.basketName ?? "";
								},
							}
						: undefined
				}
			/>
			<div className="min-h-0 flex flex-col px-4 py-6 min-w-0 flex-1 relative">
				<LocalOverlay
					show={queryInstrumentsRows.isFetching || queryProjectedList.isFetching}
					style={{
						background: rows.length === 0 ? "white" : "transparent",
						zIndex: 5,
					}}
				>
					<CircularProgressBar value="indeterminate" />
				</LocalOverlay>
				<div className="flex items-center justify-between overflow-x-auto overflow-y-hidden gap-4">
					<div className="flex fex-1 items-center gap-0.5 grow shrink-0">
						<Text type="Body/L/Book">Selected pool:</Text>
						<Text type="Body/M/Medium">
							{instrumentPool === InstrumentPool.instrumentCustomisation ? "My Instruments" : "MDOTM"}
						</Text>
						<div className={`mx-2 border-r h-4 border-r-[color:var(--palette-N1000)]`} />
						<ReactQueryWrapperBase query={queryProjectedList} loadingFallback={<></>}>
							{(_, rq) => (
								<BulkActions
									count={selectionCtx.selection.size}
									actions={[
										{
											label: "Manage selection",
											disabled: rq.isFetching,
											dropdownItems: [
												{
													label: total === 1 ? `Select one instrument` : `Select all ${total} instruments`,
													onClick: () => {
														selectionCtx.setSelection(queryProjectedList.data ?? []);
														const api = gridEl?.api;
														if (!api) {
															console.warn("ag grid not ready yet");
															return;
														}

														isRestoringSelection.current = true;
														api.selectAll();
														isRestoringSelection.current = false;
													},
												},
												{
													label: `Unselect`,
													onClick: () => {
														selectionCtx.setSelection([]);
														const api = gridEl?.api;
														if (!api) {
															console.warn("ag grid not ready yet");
															return;
														}

														isRestoringSelection.current = true;
														api.deselectAll();
														isRestoringSelection.current = false;
													},
												},
											],
										},
									]}
								/>
							)}
						</ReactQueryWrapperBase>
					</div>
					<Pagination skip={skip.value} take={take.value} total={total ?? 0} onChange={skip.set} className="shrink-0" />
				</div>
				<InstrumentBlockTable
					key={instrumentPool}
					rows={rows}
					selectionCtx={selectionCtx}
					columns={initialColumns}
					minimumSummary={minimumSummary}
					isFetching={queryInstrumentsRows.isFetching}
					search={query.value}
					onSortChanged={(newOrderBy) => orderBy.set(newOrderBy ? [newOrderBy] : undefined)}
					onSelectionChange={(instruments) => {
						if (isRestoringSelection.current || queryProjectedList.isFetching) {
							return;
						}

						const selectionToMerge = instruments.map((x) => x.tickerName).filter((x) => x != null);
						const selectionToRemove = rows
							.filter((x) => x.tickerName && !selectionToMerge.includes(x.tickerName))
							.map((x) => x.tickerName!);

						let newSelection = Set(selectionCtx.selection.merge(selectionToMerge)); // add new selection
						if (selectionToRemove.length > 0) {
							for (const tickerName of selectionToRemove) {
								newSelection = newSelection.remove(tickerName);
							}
						}

						selectionCtx.setSelection(newSelection);
					}}
					innerRef={setGridEl}
					onStateUpdated={persistColsProps.onStateUpdated}
					onGridReady={(e) => {
						persistColsProps.onGridReady(e);

						isRestoringSelection.current = true;
						syncAgSelectionWithCtx(e.api, selectionCtx);
						isRestoringSelection.current = false;
					}}
					{...rest}
				/>
			</div>
		</div>
	);
}

type InstrumentTableAdderProps = Pick<BaseHScrollTableProps<UserInstrumentDto, string>, "rows"> & {
	minimumSummary: MinimumEntitySummary;
	onSubmitMissingInstrument?: AddMissingInstrumentDialogProps["onSubmit"];
	instrumentsSetAlreadySelected: Set<string>;
	isFetching?: boolean;
	search?: string;
	onSortChanged(newOrderBy: OrderBy | undefined): void;
	onSelectionChange(instruments: UserInstrumentDto[]): void;
	innerRef: Ref<AgGridReact<UserInstrumentDto>>;
	selectionCtx: MultiSelectCtx<string>;
	columns: ColDef<UserInstrumentDto>[];
	onGridReady?(event: GridReadyEvent<UserInstrumentDto>): void;
	onStateUpdated?(event: StateUpdatedEvent<UserInstrumentDto, any>): void;
} & InstrumentAdderDialogProps;

/** @internal — exported for tests */
export function syncAgSelectionWithCtx(api: GridApi<UserInstrumentDto>, selectionCtx: MultiSelectCtx<string>) {
	api.deselectAll();
	api.forEachNode((node) => {
		if (node.data?.tickerName && selectionCtx.selection.has(node.data.tickerName)) {
			node.setSelected(true);
		}
	});
}

// refactor this table, decouple empty state with table
function InstrumentBlockTable(props: InstrumentTableAdderProps) {
	const {
		rows,
		minimumSummary,
		onSubmitMissingInstrument,
		instrumentsAlreadySelected,
		onSortChanged,
		isFetching,
		search,
		onSelectionChange,
		innerRef,
		selectionCtx,
		columns,
		onGridReady,
		onStateUpdated,
	} = props;

	const { entity } = minimumSummary;

	const hasLinkedUniverse = useMemo(() => {
		if (minimumSummary.entity === "INVESTMENT" || minimumSummary.entity === "INVESTMENT_ENHANCEMENT") {
			return minimumSummary.uuid && minimumSummary.universeIdentifier;
		}
		return false;
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [minimumSummary.entity, minimumSummary.uuid]);

	const _onSortChanged = useCallback(
		(params: SortChangedEvent<UserInstrumentDto, any>) => {
			const sortedColumn = params.columns?.find((x) => x.getSort() != null);
			const newOrderBy = sortedColumn
				? ({
						columnName: sortedColumn.getColId(),
						direction: sortedColumn.getSort()!,
					} satisfies OrderBy)
				: undefined;
			onSortChanged(newOrderBy);
		},
		[onSortChanged],
	);

	const _onSelectionChange = useCallback(
		(params: SelectionChangedEvent<UserInstrumentDto>) => {
			onSelectionChange(params.api.getSelectedRows());
		},
		[onSelectionChange],
	);

	const { invoke: resizeGrid } = useDebounced(
		(e: GridSizeChangedEvent<UserInstrumentDto, any>) => {
			if (e.clientWidth < 400) {
				e.api.sizeColumnsToFit({
					columnLimits: [
						{ key: "ag-Grid-SelectionColumn", minWidth: 48 },
						{ key: "NAME", minWidth: 150 },
						{ key: "ACTION", maxWidth: 48, minWidth: 48 },
					],
					defaultMinWidth: 150,
				});
			} else if (e.clientHeight > 400 && e.clientWidth < 640) {
				e.api.sizeColumnsToFit({
					columnLimits: [
						{ key: "NAME", minWidth: 150 },
						{ key: "DESCRIPTION", maxWidth: 250 },
						{ key: "ACTION", maxWidth: 48, minWidth: 48 },
					],
					defaultMinWidth: 150,
				});
			}
		},
		{ debounceInterval: seconds(0.5) },
	);

	if (rows.length === 0 && !isFetching && !hasLinkedUniverse) {
		return (
			<IconWallBase>
				<div className="flex-1 flex flex-col justify-center space-y-2">
					<Text as="p" type="Body/L/Bold" className="text-center">
						No results matching the selected criteria
					</Text>
					{onSubmitMissingInstrument && (
						<Button
							size="small"
							palette="secondary"
							className="mx-auto"
							onClick={() => {
								const currentInstrumentSelection =
									rows?.flatMap((x) =>
										x.tickerName && selectionCtx.selection.has(x.tickerName) && x.identifiers?.ALIAS
											? [x.identifiers?.ALIAS]
											: [],
									) ?? [];
								const aliasInComposition = instrumentsAlreadySelected.flatMap((x) => (x.alias ? [x.alias] : []));
								const instrumentInComposition = Set<string>(aliasInComposition.concat(currentInstrumentSelection));
								spawnInstrumentDialog({
									instrumentInComposition,
									defaultIdentifier: search,
									onSubmit: onSubmitMissingInstrument,
									entity,
								});
							}}
						>
							Add instrument manually
						</Button>
					)}
				</div>
			</IconWallBase>
		);
	}

	return (
		<AgGridWithModules<UserInstrumentDto>
			{...agExtra.checkboxColumn({
				mode: "multiRow",
				selectAll: "all",
				hideDisabledCheckboxes: false,
				isRowSelectable(row) {
					if (!row.data?.tickerName) {
						return false;
					}
					return !instrumentsAlreadySelected.some((inst) => inst.tickerName === row.data!.tickerName!);
				},
			})}
			columnDefs={columns}
			rowData={rows}
			getRowId={(row) => row.data.tickerName!}
			maxConcurrentDatasourceRequests={1}
			onSortChanged={_onSortChanged}
			onSelectionChanged={_onSelectionChange}
			innerRef={innerRef}
			onGridReady={onGridReady}
			onStateUpdated={onStateUpdated}
			rowClassRules={{
				[`opacity-50`]: (params) => {
					return Boolean(!params.node.selectable);
				},
			}}
			onGridSizeChanged={resizeGrid}
		/>
	);
}

export type SpawnInstrumentAdderDialogProps = Omit<InstrumentAdderDialogProps, "show" | "onClose">;
export function spawnInstrumentAddersDialog(props: SpawnInstrumentAdderDialogProps): SpawnResult<void> {
	return spawn<void>(
		adaptAnimatedNodeProvider(({ show, resolve, onHidden }) => (
			<InstrumentAddersDialog
				{...props}
				show={show}
				onAnimationStateChange={(state) => state === "hidden" && onHidden()}
				onClose={() => resolve()}
				onSubmitAsync={async (instruments) => {
					await props.onSubmitAsync(instruments);
					resolve();
				}}
			/>
		)),
	);
}
